import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Phone, Search, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { COUNTRY_LIST, CountryData } from '@/lib/countries';

interface CountryPhoneInputProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
}

const CountryPhoneInput: React.FC<CountryPhoneInputProps> = ({
    value,
    onChange,
    className = "",
    placeholder = "333 123 4567",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    // 1. Determina il paese selezionato
    const selectedCountry = useMemo(() => {
        if (!value) return COUNTRY_LIST.find(c => c.code === 'IT')!;
        const match = COUNTRY_LIST
            .sort((a, b) => b.dial_code.length - a.dial_code.length)
            .find(c => value.startsWith(c.dial_code));

        return match || COUNTRY_LIST.find(c => c.code === 'IT')!;
    }, [value]);

    const rawNumber = value.startsWith(selectedCountry.dial_code)
        ? value.slice(selectedCountry.dial_code.length)
        : value;

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputVal = e.target.value.replace(/[^0-9]/g, '');
        onChange(`${selectedCountry.dial_code}${inputVal}`);
    };

    const handleCountrySelect = (country: CountryData) => {
        setIsOpen(false);
        setSearchTerm("");
        onChange(`${country.dial_code}${rawNumber}`);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredCountries = useMemo(() => {
        const lowerTerm = searchTerm.toLowerCase();
        return COUNTRY_LIST.filter(c =>
            c.name.toLowerCase().includes(lowerTerm) ||
            c.dial_code.includes(lowerTerm) ||
            c.code.toLowerCase().includes(lowerTerm)
        );
    }, [searchTerm]);

    return (
        <div className={`relative group ${disabled ? 'opacity-60 pointer-events-none' : ''} ${className}`} ref={containerRef}>
            {/* <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-clip-500 transition-colors z-10" size={18} /> */}

            <div className="flex w-full bg-gray-50 border border-gray-200 rounded-xl focus-within:border-clip-500 focus-within:ring-2 focus-within:ring-clip-200 transition-all overflow-hidden">

                {/* Tasto Selettore Paese */}
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    disabled={disabled}
                    className="pl-4 pr-2 py-3.5 flex items-center gap-2 hover:bg-gray-100 transition-colors border-r border-gray-200 bg-gray-50/50" // pl-11
                >
                    {/* BANDIERA TRAMITE IMMAGINE */}
                    <img
                        src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w80/${selectedCountry.code.toLowerCase()}.png 2x`}
                        width="24"
                        height="16"
                        alt={selectedCountry.name}
                        className="w-6 h-auto object-contain rounded-[2px]"
                    />

                    <span className="text-sm font-bold text-gray-700">{selectedCountry.dial_code}</span>
                    <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Input Numero */}
                <input
                    type="tel"
                    value={rawNumber}
                    onChange={handleNumberChange}
                    disabled={disabled}
                    className="flex-1 px-4 py-3.5 bg-transparent outline-none font-medium text-gray-900 placeholder:text-gray-400 min-w-0"
                    placeholder={placeholder}
                />
            </div>

            {/* Menu a Tendina */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-1 w-80 z-50 bg-white border border-gray-100 rounded-xl shadow-xl max-h-60 flex flex-col overflow-hidden"
                    >
                        <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                    type="text"
                                    className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:border-clip-500 focus:ring-1 focus:ring-clip-500 placeholder:text-gray-400"
                                    placeholder="Cerca paese..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto custom-scrollbar p-1">
                            {filteredCountries.map((country) => (
                                <button
                                    key={country.code}
                                    type="button"
                                    onClick={() => handleCountrySelect(country)}
                                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-colors ${selectedCountry.code === country.code
                                        ? 'bg-clip-50 text-clip-700 font-bold'
                                        : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        {/* BANDIERA NELLA LISTA */}
                                        <img
                                            src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                                            alt={country.name}
                                            className="w-5 h-auto object-contain rounded-[2px] shadow-sm"
                                            loading="lazy"
                                        />
                                        <span className="truncate">{country.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-400 text-xs font-mono">{country.dial_code}</span>
                                        {selectedCountry.code === country.code && <Check size={14} />}
                                    </div>
                                </button>
                            ))}
                            {filteredCountries.length === 0 && (
                                <div className="px-3 py-8 text-center text-sm text-gray-400 flex flex-col items-center gap-2">
                                    <span className="text-2xl">🌍</span>
                                    Nessun paese trovato
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CountryPhoneInput;